import axios from 'axios';
import { isEmpty, isObjectLike } from 'lodash';

import config from 'trade-in/config/index.js';
import Logger from '@sm360/logger-sentry';
import { ConfigsService, HTTPService, UrlService } from '@sm360/widget-commons-core-services';
import { UtilsService } from '@sm360/widget-commons-general';

/* eslint-disable no-underscore-dangle */

/**
 * Services talking to the ShowroomAPI for Canadian Black Books APIs
 * @class ShowroomCBBService
 */
class ShowroomCBBService {
    constructor() {
        /**
         * @type {String} Base path for api calls
         */
        this._basePath = `${config.endpoint.showroom}/service/v1`;

        /**
         * @type {String} Authentication parameters to add in the URI
         */
        this._authParameters = 'username=public&password=public';

        this.logger = new Logger('ShowroomCBBService', ConfigsService.getLoggerContext());
    }

    _getDataForEstimation(tradeInVehicle) {
        if (!tradeInVehicle || !isObjectLike(tradeInVehicle) || isEmpty(tradeInVehicle)) {
            return null;
        }

        const km = tradeInVehicle.odometer || this._getDefaultOdometerValue(tradeInVehicle.year);

        return {
            year: String(tradeInVehicle.year),
            make: tradeInVehicle.make,
            model: tradeInVehicle.model,
            trim: tradeInVehicle.trim,
            style: tradeInVehicle.style,
            kilometers: String(km),
        };
    }

    /**
     * Private method to get an approximated odometer value for a given model year vehicle
     * @memberof ShowroomCBBService
     * @param {Number} [vehicleYear] model year of the vehicle
     * @param {?Number} [averageMileageByYear=15000] value of average odometer by year. Default: 15000. (optional)
     * @return {?Number} Integer or null
     */
    _getDefaultOdometerValue(vehicleYear, averageMileageByYear = 15000) {
        const year = UtilsService.getIntegerOrNull(vehicleYear);
        if (!year || year < 1900) {
            throw new TypeError('argument [vehicleYear] should be a safe integer higher than 1900');
        }

        const defaultMileageByYear = 15000;
        const currentYear = new Date().getFullYear();

        let odometerByYear = UtilsService.getIntegerOrNull(averageMileageByYear);
        if (!odometerByYear || odometerByYear <= 0) {
            odometerByYear = defaultMileageByYear;
        }

        let yearDiff = currentYear - year;
        if (yearDiff < -1) {
            throw new TypeError('could not obtain a negative or null value');
        }
        yearDiff = yearDiff < 0 ? -0.5 : yearDiff;

        return Math.floor((yearDiff + 1) * odometerByYear);
    }

    /**
     * Call to get vehicle information for the Canadian Black Book autocomplete
     * @memberof ShowroomCBBService
     * @param {String} [hint] The value searched
     * @param {Number} [limit=10] The desired size of the result. Default: 10
     * @returns {Promise} An array of vehicle details
     */
    searchAutocomplete(hint, limit = 10) {
        if (!hint) {
            return Promise.resolve([]);
        }

        const url =
            `${this._basePath}/vehicleEvaluation.autoComplete?${this._authParameters}` +
            `&hint=${encodeURIComponent(hint)}&limit=${limit}`;

        return axios
            .get(url)
            .then(({ data }) => {
                if (data.error) {
                    this.logger.warn(
                        'An error occurred while doing the search for the autocomplete' +
                            ` with hint [${hint}] and limit [${limit}]`,
                        data.error
                    );
                    return Promise.reject([]);
                }
                return Promise.resolve(data);
            })
            .catch((err) => {
                this.logger.error(
                    'An error occurred while doing the search for the autocomplete' +
                        ` with hint [${hint}] and limit [${limit}]`,
                    err
                );
                return Promise.reject([]);
            });
    }

    /**
     * Call to get an estimated value of a vehicle by Canadian Black Book
     * @memberof ShowroomCBBService
     *
     */
    fetchEstimation(tradeInVehicle) {
        return new Promise((resolve, reject) => {
            const location = ConfigsService.getOrganizationUnitLocation();
            const obj = this._getDataForEstimation(tradeInVehicle);

            if (!obj) {
                return reject(
                    HTTPService.formatRejectResponse('invalidParams', {
                        status: 400,
                    })
                );
            }

            const url =
                `${this._basePath}/vehicleEvaluation?${this._authParameters}` +
                `&${UrlService.buildUriParametersFromCollection(obj)}&location=${location}`;

            return axios
                .get(url)
                .then(({ data }) => {
                    if (data.error) {
                        this.logger.error('An error occurred while fetching CBB estimation', data.error);
                        return reject(data.error);
                    }
                    return resolve(data);
                })
                .catch((err) => {
                    this.logger.error('An error occurred while fetching CBB estimation', err);
                    return reject(err);
                });
        });
    }
}

export default new ShowroomCBBService();



// WEBPACK FOOTER //
// ./src/services/ShowroomCBBService/ShowroomCBBService.js